/**
 * "Add someone": a JV partner, a ClickBank pro, a creator who said they'd
 * share. People in our own program show up by themselves every night; this
 * is for everyone else (and for people we're still talking to).
 */
import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button, FieldHint, Input, Label, Modal, ModalFooter, Select, Textarea, useToast } from "@/components/ui";
import { CHANNELS, CHANNEL_FALLBACK, KINDS, KIND_META, PLATFORMS, kindBlurb, kindLabel, memberName, useTeam } from "./shared";
import type { Affiliate, AffiliateChannel, AffiliateKind, Rhythm } from "./types";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AddAffiliateModal({
  open, onClose, onCreated, rhythm, channels, initialKind,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (a: Affiliate) => void;
  rhythm?: Partial<Record<string, Rhythm>> | null;
  channels?: Partial<Record<string, string>> | null;
  initialKind?: AffiliateKind | null;
}) {
  const toast = useToast();
  const team = useTeam();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState<AffiliateKind>("casual");
  const [channel, setChannel] = useState<AffiliateChannel>("syncsuite");
  const [channelNote, setChannelNote] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [handle, setHandle] = useState("");
  const [owner, setOwner] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setEmail(""); setKind(initialKind ?? "casual"); setChannel("syncsuite"); setChannelNote("");
    setPlatform("instagram"); setHandle(""); setOwner(""); setNotes(""); setTried(false);
  }, [open, initialKind]);

  const emailBad = email.trim() !== "" && !EMAIL.test(email.trim());
  const nameMissing = name.trim() === "";

  const save = async () => {
    setTried(true);
    if (nameMissing || emailBad) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        email: email.trim() || null,
        kind,
        channel,
        channel_note: channel !== "syncsuite" && channelNote.trim() ? channelNote.trim() : null,
        handles: handle.trim() ? [{ platform, handle: handle.trim().replace(/^@/, "") }] : [],
        notes: notes.trim() || null,
      };
      if (owner) body.owner_id = owner;
      const d = await api.post<{ affiliate: Affiliate }>("/affiliates", body);
      toast.success(`Added ${d.affiliate.name}`);
      onCreated(d.affiliate);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add them");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Add someone" description="Anyone who sends us customers, or who you're talking to about it.">
      <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {/* Scrolls on its own so the buttons stay reachable on a phone. */}
        <div className="-mx-5 max-h-[calc(100dvh-13rem)] space-y-4 overflow-y-auto px-5 pb-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="aff-name" required>Name</Label>
            <Input id="aff-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Sarah Lee" invalid={tried && nameMissing} />
            {tried && nameMissing && <FieldHint error>Give them a name.</FieldHint>}
          </div>
          <div>
            <Label htmlFor="aff-email">Email</Label>
            <Input id="aff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sarah@example.com" invalid={emailBad && tried} />
            {emailBad && tried && <FieldHint error>That email doesn't look right.</FieldHint>}
          </div>
        </div>

        <div>
          <Label>What kind of relationship?</Label>
          <div className="grid gap-2 sm:grid-cols-2">
            {KINDS.map((k) => {
              const meta = KIND_META[k];
              const Icon = meta.icon;
              const on = kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setKind(k)}
                  className={cn(
                    "flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors",
                    on ? "border-indigo-400/60 bg-indigo-500/10 ring-1 ring-indigo-400/30" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700",
                  )}
                >
                  <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", meta.iconClass)} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-zinc-100">{kindLabel(k, rhythm)}</span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">{kindBlurb(k, rhythm)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="aff-channel">Where they sell us</Label>
            <Select id="aff-channel" value={channel} onChange={(e) => setChannel(e.target.value as AffiliateChannel)}>
              {CHANNELS.map((c) => <option key={c} value={c}>{channels?.[c] ?? CHANNEL_FALLBACK[c]}</option>)}
            </Select>
          </div>
          {channel !== "syncsuite" && (
            <div>
              <Label htmlFor="aff-channel-note">{channel === "clickbank" ? "Their ClickBank name (optional)" : "Where exactly?"}</Label>
              <Input
                id="aff-channel-note"
                value={channelNote}
                onChange={(e) => setChannelNote(e.target.value)}
                placeholder={channel === "clickbank" ? "e.g. sarahsells" : "e.g. AppSumo, their own course"}
              />
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]">
          <div>
            <Label htmlFor="aff-platform">Their main social</Label>
            <Select id="aff-platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="aff-handle">Handle</Label>
            <Input id="aff-handle" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@sarahgrows" />
          </div>
        </div>

        <div>
          <Label htmlFor="aff-owner">Who looks after them</Label>
          <Select id="aff-owner" value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Me</option>
            {team.map((m) => <option key={m.id} value={m.id}>{memberName(m.name)}</option>)}
          </Select>
        </div>

        <div>
          <Label htmlFor="aff-notes">Notes</Label>
          <Textarea id="aff-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="How you met, what they're good at, what they asked for" />
        </div>
        </div>

        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={saving} leftIcon={<UserPlus className="h-4 w-4" />}>Add them</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
